import { defineStore } from 'pinia'
import { ref, computed, watch } from 'vue'
import { useRouter, useRoute } from 'vue-router'
import { useFilterPresets, type FilterPreset } from '@/composables/useFilterPresets'

export interface TaskFilterState {
  search: string
  status: string[]
  priority: string[]
  tags: string[]
  ownerIds: string[]
  projectIds: string[]
  personIds: string[]
  // Excluded items per filter (per-item granularity)
  excludedStatuses: string[]
  excludedTags: string[]
  excludedOwnerIds: string[]
  excludedProjectIds: string[]
  excludedPersonIds: string[]
  // Date filters
  overdue: boolean
  startDateFrom: string
  startDateTo: string
  plannedDateFrom: string
  plannedDateTo: string
  deadlineFrom: string
  deadlineTo: string
  completedDateFrom: string
  completedDateTo: string
  // Archive filter
  includeArchived: boolean
  archivedOnly: boolean
}

const STORAGE_KEY = 'kb-task-filters'

// URL param names
const URL_PARAMS = {
  search: 'q',
  status: 'status',
  priority: 'priority',
  tags: 'tags',
  ownerIds: 'owners',
  projectIds: 'project',
  personIds: 'persons',
  overdue: 'overdue',
  startDateFrom: 'startFrom',
  startDateTo: 'startTo',
  plannedDateFrom: 'plannedFrom',
  plannedDateTo: 'plannedTo',
  deadlineFrom: 'deadlineFrom',
  deadlineTo: 'deadlineTo',
  completedDateFrom: 'completedFrom',
  completedDateTo: 'completedTo',
  includeArchived: 'includeArchived',
  archivedOnly: 'archivedOnly',
} as const

// URL param names for excluded items
const URL_EXCLUDE_PARAMS = {
  excludedStatuses: 'status_not',
  excludedTags: 'tags_not',
  excludedOwnerIds: 'owners_not',
  excludedProjectIds: 'project_not',
  excludedPersonIds: 'persons_not',
} as const

function getDefaultState(): TaskFilterState {
  return {
    search: '',
    status: [],
    priority: [],
    tags: [],
    ownerIds: [],
    projectIds: [],
    personIds: [],
    excludedStatuses: [],
    excludedTags: [],
    excludedOwnerIds: [],
    excludedProjectIds: [],
    excludedPersonIds: [],
    overdue: false,
    startDateFrom: '',
    startDateTo: '',
    plannedDateFrom: '',
    plannedDateTo: '',
    deadlineFrom: '',
    deadlineTo: '',
    completedDateFrom: '',
    completedDateTo: '',
    includeArchived: false,
    archivedOnly: false,
  }
}

function parseArrayParam(value: string | null): string[] {
  if (!value) return []
  return value.split(',').filter(Boolean)
}

function serializeArrayParam(arr: string[]): string | undefined {
  return arr.length > 0 ? arr.join(',') : undefined
}

export const useTaskFilterStore = defineStore('taskFilter', () => {
  // Filter state — included items
  const search = ref('')
  const status = ref<string[]>([])
  const priority = ref<string[]>([])
  const tags = ref<string[]>([])
  const ownerIds = ref<string[]>([])
  const projectIds = ref<string[]>([])
  const personIds = ref<string[]>([])

  // Excluded items per filter (per-item granularity)
  const excludedStatuses = ref<string[]>([])
  const excludedTags = ref<string[]>([])
  const excludedOwnerIds = ref<string[]>([])
  const excludedProjectIds = ref<string[]>([])
  const excludedPersonIds = ref<string[]>([])

  // Archive filters
  const includeArchived = ref(false)
  const archivedOnly = ref(false)

  // Date filters
  const overdue = ref(false)
  const startDateFrom = ref('')
  const startDateTo = ref('')
  const plannedDateFrom = ref('')
  const plannedDateTo = ref('')
  const deadlineFrom = ref('')
  const deadlineTo = ref('')
  const completedDateFrom = ref('')
  const completedDateTo = ref('')

  // Initialization flag
  const initialized = ref(false)
  const urlSyncEnabled = ref(true)

  // Presets integration
  const {
    presets,
    loading: presetsLoading,
    loadPresets,
    savePreset: savePresetApi,
    deletePreset: deletePresetApi,
    getPreset,
  } = useFilterPresets()

  // Computed: current filters object
  const filters = computed<TaskFilterState>(() => ({
    search: search.value,
    status: status.value,
    priority: priority.value,
    tags: tags.value,
    ownerIds: ownerIds.value,
    projectIds: projectIds.value,
    personIds: personIds.value,
    excludedStatuses: excludedStatuses.value,
    excludedTags: excludedTags.value,
    excludedOwnerIds: excludedOwnerIds.value,
    excludedProjectIds: excludedProjectIds.value,
    excludedPersonIds: excludedPersonIds.value,
    overdue: overdue.value,
    startDateFrom: startDateFrom.value,
    startDateTo: startDateTo.value,
    plannedDateFrom: plannedDateFrom.value,
    plannedDateTo: plannedDateTo.value,
    deadlineFrom: deadlineFrom.value,
    deadlineTo: deadlineTo.value,
    completedDateFrom: completedDateFrom.value,
    completedDateTo: completedDateTo.value,
    includeArchived: includeArchived.value,
    archivedOnly: archivedOnly.value,
  }))

  // Computed: check if any filter is active
  const hasActiveFilters = computed(() => {
    return !!(
      search.value ||
      status.value.length > 0 ||
      priority.value.length > 0 ||
      tags.value.length > 0 ||
      ownerIds.value.length > 0 ||
      projectIds.value.length > 0 ||
      personIds.value.length > 0 ||
      excludedStatuses.value.length > 0 ||
      excludedTags.value.length > 0 ||
      excludedOwnerIds.value.length > 0 ||
      excludedProjectIds.value.length > 0 ||
      excludedPersonIds.value.length > 0 ||
      overdue.value ||
      startDateFrom.value ||
      startDateTo.value ||
      plannedDateFrom.value ||
      plannedDateTo.value ||
      deadlineFrom.value ||
      deadlineTo.value ||
      completedDateFrom.value ||
      completedDateTo.value ||
      includeArchived.value ||
      archivedOnly.value
    )
  })

  // Computed: count of active filters
  const activeFiltersCount = computed(() => {
    let count = 0
    if (search.value) count++
    if (status.value.length > 0 || excludedStatuses.value.length > 0) count++
    if (priority.value.length > 0) count++
    if (tags.value.length > 0 || excludedTags.value.length > 0) count++
    if (ownerIds.value.length > 0 || excludedOwnerIds.value.length > 0) count++
    if (projectIds.value.length > 0 || excludedProjectIds.value.length > 0) count++
    if (personIds.value.length > 0 || excludedPersonIds.value.length > 0) count++
    return count
  })

  // Get current filters in preset format
  function getCurrentFiltersForPreset(): FilterPreset['filters'] {
    return {
      search: search.value || undefined,
      status: status.value.length > 0 ? status.value.join(',') : undefined,
      priority: priority.value.length > 0 ? priority.value.join(',') : undefined,
      tags: tags.value.length > 0 ? tags.value : undefined,
      ownerIds: ownerIds.value.length > 0 ? ownerIds.value : undefined,
      projectIds: projectIds.value.length > 0 ? projectIds.value : undefined,
      personIds: personIds.value.length > 0 ? personIds.value : undefined,
      excludedStatuses: excludedStatuses.value.length > 0 ? excludedStatuses.value : undefined,
      excludedTags: excludedTags.value.length > 0 ? excludedTags.value : undefined,
      excludedOwnerIds: excludedOwnerIds.value.length > 0 ? excludedOwnerIds.value : undefined,
      excludedProjectIds: excludedProjectIds.value.length > 0 ? excludedProjectIds.value : undefined,
      excludedPersonIds: excludedPersonIds.value.length > 0 ? excludedPersonIds.value : undefined,
    }
  }

  // Set filters from state object
  function setFilters(state: Partial<TaskFilterState>) {
    if (state.search !== undefined) search.value = state.search
    if (state.status !== undefined) status.value = state.status
    if (state.priority !== undefined) priority.value = state.priority
    if (state.tags !== undefined) tags.value = state.tags
    if (state.ownerIds !== undefined) ownerIds.value = state.ownerIds
    if (state.projectIds !== undefined) projectIds.value = state.projectIds
    if (state.personIds !== undefined) personIds.value = state.personIds
    if (state.excludedStatuses !== undefined) excludedStatuses.value = state.excludedStatuses
    if (state.excludedTags !== undefined) excludedTags.value = state.excludedTags
    if (state.excludedOwnerIds !== undefined) excludedOwnerIds.value = state.excludedOwnerIds
    if (state.excludedProjectIds !== undefined) excludedProjectIds.value = state.excludedProjectIds
    if (state.excludedPersonIds !== undefined) excludedPersonIds.value = state.excludedPersonIds
    if (state.overdue !== undefined) overdue.value = state.overdue
    if (state.startDateFrom !== undefined) startDateFrom.value = state.startDateFrom
    if (state.startDateTo !== undefined) startDateTo.value = state.startDateTo
    if (state.plannedDateFrom !== undefined) plannedDateFrom.value = state.plannedDateFrom
    if (state.plannedDateTo !== undefined) plannedDateTo.value = state.plannedDateTo
    if (state.deadlineFrom !== undefined) deadlineFrom.value = state.deadlineFrom
    if (state.deadlineTo !== undefined) deadlineTo.value = state.deadlineTo
    if (state.completedDateFrom !== undefined) completedDateFrom.value = state.completedDateFrom
    if (state.completedDateTo !== undefined) completedDateTo.value = state.completedDateTo
    if (state.includeArchived !== undefined) includeArchived.value = state.includeArchived
    if (state.archivedOnly !== undefined) archivedOnly.value = state.archivedOnly
  }

  // Clear all filters
  function clearFilters() {
    const defaults = getDefaultState()
    search.value = defaults.search
    status.value = defaults.status
    priority.value = defaults.priority
    tags.value = defaults.tags
    ownerIds.value = defaults.ownerIds
    projectIds.value = defaults.projectIds
    personIds.value = defaults.personIds
    excludedStatuses.value = []
    excludedTags.value = []
    excludedOwnerIds.value = []
    excludedProjectIds.value = []
    excludedPersonIds.value = []
    overdue.value = false
    startDateFrom.value = ''
    startDateTo.value = ''
    plannedDateFrom.value = ''
    plannedDateTo.value = ''
    deadlineFrom.value = ''
    deadlineTo.value = ''
    completedDateFrom.value = ''
    completedDateTo.value = ''
    includeArchived.value = false
    archivedOnly.value = false
  }

  // Apply a preset
  function applyPreset(preset: FilterPreset) {
    search.value = preset.filters.search || ''
    // Support legacy single-value status/priority from saved presets
    status.value = preset.filters.status
      ? (typeof preset.filters.status === 'string' ? [preset.filters.status] : preset.filters.status as unknown as string[])
      : []
    priority.value = preset.filters.priority
      ? (typeof preset.filters.priority === 'string' ? [preset.filters.priority] : preset.filters.priority as unknown as string[])
      : []
    tags.value = preset.filters.tags || []
    ownerIds.value = preset.filters.ownerIds || []
    projectIds.value = preset.filters.projectIds || []
    // Support both new personIds and legacy personId
    if (preset.filters.personIds && preset.filters.personIds.length > 0) {
      personIds.value = [...preset.filters.personIds]
    } else if (preset.filters.personId) {
      personIds.value = [preset.filters.personId]
    } else {
      personIds.value = []
    }
    // Restore excluded items (new per-item format)
    excludedStatuses.value = preset.filters.excludedStatuses || []
    excludedTags.value = preset.filters.excludedTags || []
    excludedOwnerIds.value = preset.filters.excludedOwnerIds || []
    excludedProjectIds.value = preset.filters.excludedProjectIds || []
    excludedPersonIds.value = preset.filters.excludedPersonIds || []
    // Legacy migration: if old boolean flags are present and no new arrays, move all items to excluded
    if (!preset.filters.excludedTags && preset.filters.excludeTags && preset.filters.tags?.length) {
      excludedTags.value = [...preset.filters.tags]
      tags.value = []
    }
    if (!preset.filters.excludedOwnerIds && preset.filters.excludeOwnerIds && preset.filters.ownerIds?.length) {
      excludedOwnerIds.value = [...preset.filters.ownerIds]
      ownerIds.value = []
    }
  }

  // Save current filters as preset
  async function savePreset(name: string): Promise<FilterPreset | null> {
    return savePresetApi(name, getCurrentFiltersForPreset())
  }

  // Delete a preset
  async function deletePreset(id: string): Promise<boolean> {
    return deletePresetApi(id)
  }

  // Save to localStorage
  function saveToStorage() {
    try {
      localStorage.setItem(STORAGE_KEY, JSON.stringify(filters.value))
    } catch (e) {
      console.warn('[TaskFilter] Failed to save to localStorage:', e)
    }
  }

  // Load from localStorage (with legacy migration)
  function loadFromStorage(): TaskFilterState | null {
    try {
      const stored = localStorage.getItem(STORAGE_KEY)
      if (stored) {
        const parsed = JSON.parse(stored)
        // Migrate legacy single-value fields to arrays
        const result: TaskFilterState = {
          search: parsed.search || '',
          status: Array.isArray(parsed.status) ? parsed.status : parsed.status ? [parsed.status] : [],
          priority: Array.isArray(parsed.priority) ? parsed.priority : parsed.priority ? [parsed.priority] : [],
          tags: parsed.tags || [],
          ownerIds: parsed.ownerIds || [],
          projectIds: Array.isArray(parsed.projectIds) ? parsed.projectIds : parsed.projectId ? [parsed.projectId] : [],
          personIds: parsed.personIds || [],
          excludedStatuses: parsed.excludedStatuses || [],
          excludedTags: parsed.excludedTags || [],
          excludedOwnerIds: parsed.excludedOwnerIds || [],
          excludedProjectIds: parsed.excludedProjectIds || [],
          excludedPersonIds: parsed.excludedPersonIds || [],
          overdue: parsed.overdue || false,
          startDateFrom: parsed.startDateFrom || '',
          startDateTo: parsed.startDateTo || '',
          plannedDateFrom: parsed.plannedDateFrom || '',
          plannedDateTo: parsed.plannedDateTo || '',
          deadlineFrom: parsed.deadlineFrom || '',
          deadlineTo: parsed.deadlineTo || '',
          completedDateFrom: parsed.completedDateFrom || '',
          completedDateTo: parsed.completedDateTo || '',
          includeArchived: parsed.includeArchived || false,
          archivedOnly: parsed.archivedOnly || false,
        }
        // Legacy migration: boolean excludeX flags → move all items to excluded arrays
        if (parsed.excludeTags === true && result.excludedTags.length === 0 && result.tags.length > 0) {
          result.excludedTags = [...result.tags]
          result.tags = []
        }
        if (parsed.excludeOwnerIds === true && result.excludedOwnerIds.length === 0 && result.ownerIds.length > 0) {
          result.excludedOwnerIds = [...result.ownerIds]
          result.ownerIds = []
        }
        if (parsed.excludeProjectIds === true && result.excludedProjectIds.length === 0 && result.projectIds.length > 0) {
          result.excludedProjectIds = [...result.projectIds]
          result.projectIds = []
        }
        if (parsed.excludePersonIds === true && result.excludedPersonIds.length === 0 && result.personIds.length > 0) {
          result.excludedPersonIds = [...result.personIds]
          result.personIds = []
        }
        return result
      }
    } catch (e) {
      console.warn('[TaskFilter] Failed to load from localStorage:', e)
    }
    return null
  }

  // Sync state to URL params
  function syncToUrl(router: ReturnType<typeof useRouter>) {
    if (!urlSyncEnabled.value) return

    const query: Record<string, string | undefined> = {}

    if (search.value) query[URL_PARAMS.search] = search.value
    if (status.value.length > 0) query[URL_PARAMS.status] = serializeArrayParam(status.value)
    if (priority.value.length > 0) query[URL_PARAMS.priority] = serializeArrayParam(priority.value)
    if (tags.value.length > 0) query[URL_PARAMS.tags] = serializeArrayParam(tags.value)
    if (ownerIds.value.length > 0) query[URL_PARAMS.ownerIds] = serializeArrayParam(ownerIds.value)
    if (projectIds.value.length > 0) query[URL_PARAMS.projectIds] = serializeArrayParam(projectIds.value)
    if (personIds.value.length > 0) query[URL_PARAMS.personIds] = serializeArrayParam(personIds.value)

    // Excluded items (comma-separated IDs)
    if (excludedStatuses.value.length > 0) query[URL_EXCLUDE_PARAMS.excludedStatuses] = serializeArrayParam(excludedStatuses.value)
    if (excludedTags.value.length > 0) query[URL_EXCLUDE_PARAMS.excludedTags] = serializeArrayParam(excludedTags.value)
    if (excludedOwnerIds.value.length > 0) query[URL_EXCLUDE_PARAMS.excludedOwnerIds] = serializeArrayParam(excludedOwnerIds.value)
    if (excludedProjectIds.value.length > 0) query[URL_EXCLUDE_PARAMS.excludedProjectIds] = serializeArrayParam(excludedProjectIds.value)
    if (excludedPersonIds.value.length > 0) query[URL_EXCLUDE_PARAMS.excludedPersonIds] = serializeArrayParam(excludedPersonIds.value)

    // Date filters
    if (overdue.value) query[URL_PARAMS.overdue] = 'true'
    if (startDateFrom.value) query[URL_PARAMS.startDateFrom] = startDateFrom.value
    if (startDateTo.value) query[URL_PARAMS.startDateTo] = startDateTo.value
    if (plannedDateFrom.value) query[URL_PARAMS.plannedDateFrom] = plannedDateFrom.value
    if (plannedDateTo.value) query[URL_PARAMS.plannedDateTo] = plannedDateTo.value
    if (deadlineFrom.value) query[URL_PARAMS.deadlineFrom] = deadlineFrom.value
    if (deadlineTo.value) query[URL_PARAMS.deadlineTo] = deadlineTo.value
    if (completedDateFrom.value) query[URL_PARAMS.completedDateFrom] = completedDateFrom.value
    if (completedDateTo.value) query[URL_PARAMS.completedDateTo] = completedDateTo.value
    if (includeArchived.value) query[URL_PARAMS.includeArchived] = 'true'
    if (archivedOnly.value) query[URL_PARAMS.archivedOnly] = 'true'

    // Clean undefined values
    const cleanQuery = Object.fromEntries(
      Object.entries(query).filter(([, v]) => v !== undefined)
    ) as Record<string, string>

    // All URL param names (both filter values and exclude)
    const allParamNames = [
      ...Object.values(URL_PARAMS),
      ...Object.values(URL_EXCLUDE_PARAMS),
    ]

    // Only update if different from current query
    const currentQuery = router.currentRoute.value.query
    const hasChanges = allParamNames.some(paramName => {
      return cleanQuery[paramName] !== currentQuery[paramName]
    })

    if (hasChanges) {
      router.replace({
        query: {
          ...currentQuery,
          ...cleanQuery,
          // Remove params that are now empty
          ...Object.fromEntries(
            allParamNames
              .filter(param => !cleanQuery[param] && currentQuery[param])
              .map(param => [param, undefined])
          ),
        },
      })
    }
  }

  // Load state from URL params
  function loadFromUrl(route: ReturnType<typeof useRoute>): TaskFilterState | null {
    const query = route.query

    // Check if any filter params exist in URL
    const allParams = [...Object.values(URL_PARAMS), ...Object.values(URL_EXCLUDE_PARAMS)]
    const hasFilterParams = allParams.some(param => query[param])
    if (!hasFilterParams) return null

    return {
      search: (query[URL_PARAMS.search] as string) || '',
      status: parseArrayParam(query[URL_PARAMS.status] as string),
      priority: parseArrayParam(query[URL_PARAMS.priority] as string),
      tags: parseArrayParam(query[URL_PARAMS.tags] as string),
      ownerIds: parseArrayParam(query[URL_PARAMS.ownerIds] as string),
      projectIds: parseArrayParam(query[URL_PARAMS.projectIds] as string),
      personIds: parseArrayParam(query[URL_PARAMS.personIds] as string),
      excludedStatuses: parseArrayParam(query[URL_EXCLUDE_PARAMS.excludedStatuses] as string),
      excludedTags: parseArrayParam(query[URL_EXCLUDE_PARAMS.excludedTags] as string),
      excludedOwnerIds: parseArrayParam(query[URL_EXCLUDE_PARAMS.excludedOwnerIds] as string),
      excludedProjectIds: parseArrayParam(query[URL_EXCLUDE_PARAMS.excludedProjectIds] as string),
      excludedPersonIds: parseArrayParam(query[URL_EXCLUDE_PARAMS.excludedPersonIds] as string),
      overdue: query[URL_PARAMS.overdue] === 'true',
      startDateFrom: (query[URL_PARAMS.startDateFrom] as string) || '',
      startDateTo: (query[URL_PARAMS.startDateTo] as string) || '',
      plannedDateFrom: (query[URL_PARAMS.plannedDateFrom] as string) || '',
      plannedDateTo: (query[URL_PARAMS.plannedDateTo] as string) || '',
      deadlineFrom: (query[URL_PARAMS.deadlineFrom] as string) || '',
      deadlineTo: (query[URL_PARAMS.deadlineTo] as string) || '',
      completedDateFrom: (query[URL_PARAMS.completedDateFrom] as string) || '',
      completedDateTo: (query[URL_PARAMS.completedDateTo] as string) || '',
      includeArchived: query[URL_PARAMS.includeArchived] === 'true',
      archivedOnly: query[URL_PARAMS.archivedOnly] === 'true',
    }
  }

  // Apply filters from URL params (can be called after navigation)
  function applyFromUrl(route: ReturnType<typeof useRoute>) {
    const urlState = loadFromUrl(route)
    if (urlState) {
      setFilters(urlState)
    }
  }

  // Initialize store: load from URL > localStorage > defaults
  function init(router: ReturnType<typeof useRouter>, route: ReturnType<typeof useRoute>) {
    // Already initialized - check if URL has filter params and apply them
    // This handles navigation from dashboard with filters in URL
    if (initialized.value) {
      const urlState = loadFromUrl(route)
      if (urlState) {
        // URL has filters - apply them (overrides current state)
        setFilters(urlState)
      } else if (hasActiveFilters.value) {
        // No URL filters but state has filters - sync to URL
        syncToUrl(router)
      }
      return
    }

    // Priority: URL params > localStorage > defaults
    const urlState = loadFromUrl(route)
    const storageState = loadFromStorage()

    if (urlState) {
      setFilters(urlState)
    } else if (storageState) {
      setFilters(storageState)
      // Sync restored state to URL
      syncToUrl(router)
    }

    // Load presets
    loadPresets()

    initialized.value = true

    // Watch for filter changes and sync
    watch(
      filters,
      () => {
        saveToStorage()
        syncToUrl(router)
      },
      { deep: true }
    )
  }

  // Temporarily disable URL sync (useful during programmatic updates)
  function withoutUrlSync(fn: () => void) {
    urlSyncEnabled.value = false
    try {
      fn()
    } finally {
      urlSyncEnabled.value = true
    }
  }

  return {
    // State
    search,
    status,
    priority,
    tags,
    ownerIds,
    projectIds,
    personIds,

    // Excluded items per filter
    excludedStatuses,
    excludedTags,
    excludedOwnerIds,
    excludedProjectIds,
    excludedPersonIds,

    // Archive filters
    includeArchived,
    archivedOnly,

    // Date filters
    overdue,
    startDateFrom,
    startDateTo,
    plannedDateFrom,
    plannedDateTo,
    deadlineFrom,
    deadlineTo,
    completedDateFrom,
    completedDateTo,

    // Computed
    filters,
    hasActiveFilters,
    activeFiltersCount,

    // Presets
    presets,
    presetsLoading,
    loadPresets,
    applyPreset,
    savePreset,
    deletePreset,
    getPreset,

    // Methods
    setFilters,
    clearFilters,
    getCurrentFiltersForPreset,
    init,
    applyFromUrl,
    withoutUrlSync,
  }
})
